import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useAuth } from '../hooks/useAuth'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import { Users, Plus, Edit, Trash2, Search, Mail, Calendar, Shield, Copy, ExternalLink, Check, Clock, BarChart3, PieChart, AlertCircle, Archive, RotateCcw } from 'lucide-react'
import InviteHubUserModal from '../components/Modals/InviteHubUserModal'
import EditSASUserModal from '../components/Modals/EditSASUserModal'
import ConfirmationModal from '../components/Modals/ConfirmationModal'
import sasUsersAPI from '../services/sasUsersAPI'
import hubUserInvitationsAPI from '../services/hubUserInvitationsAPI'
import toast from 'react-hot-toast'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts'

export default function SASUsers() {
  const { user, isControlCenter, hasPermission } = useAuth()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSection, setActiveSection] = useState('active') // 'active', 'pending', or 'archived'
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [userToRestore, setUserToRestore] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [copiedInvitationId, setCopiedInvitationId] = useState(null)

  const canInviteUsers = hasPermission('users.create')
  const canEditUsers = hasPermission('users.update')
  const canDeleteUsers = hasPermission('users.delete')
  const showActionColumn = canEditUsers || canDeleteUsers
  const [monthFilter, setMonthFilter] = useState(12) // Default to last 12 months

  // Fetch pending hub user invitations - optimized caching
  const { data: invitationsData, isLoading: invitationsLoading } = useQuery(
    ['hub-user-invitations-pending'],
    () => hubUserInvitationsAPI.getAll({ status: 'pending' }),
    { 
      enabled: isControlCenter(),
      staleTime: 2 * 60 * 1000, // 2 minutes (invitations change more frequently)
      cacheTime: 10 * 60 * 1000,
      refetchOnMount: false,
    }
  )
  const pendingInvitations = invitationsData?.data?.invitations || []

  // Fetch all SAS users across organizations (all statuses except archived) - optimized caching
  const { data: usersData, isLoading, refetch } = useQuery(
    ['sas-users-all', searchTerm],
    () => sasUsersAPI.getAll({ search: searchTerm }),
    { 
      enabled: isControlCenter() && activeSection === 'active',
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
    }
  )
  // Filter out archived users from main list (show active, inactive, suspended)
  const users = (usersData?.data?.users || []).filter(user => user.status !== 'archived')

  // Fetch archived SAS users (archived status only) - always fetch for count display
  const { data: archivedUsersData, isLoading: archivedUsersLoading } = useQuery(
    ['sas-users-all', searchTerm, 'archived'],
    () => sasUsersAPI.getAll({ search: searchTerm, status: 'archived' }),
    { 
      enabled: isControlCenter(),
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
    }
  )
  const archivedUsers = archivedUsersData?.data?.users || []

  // Process data for monthly user additions bar chart
  const monthlyUserData = useMemo(() => {
    const now = new Date()
    const months = []
    
    // Generate months based on filter (last N months)
    for (let i = monthFilter - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      months.push({ month: monthLabel, monthKey, count: 0 })
    }

    // Count users by month
    users.forEach(user => {
      if (user.created_at) {
        const userDate = new Date(user.created_at)
        const monthKey = `${userDate.getFullYear()}-${String(userDate.getMonth() + 1).padStart(2, '0')}`
        const monthEntry = months.find(m => m.monthKey === monthKey)
        if (monthEntry) {
          monthEntry.count++
        }
      }
    })

    // Count pending invitations by month
    pendingInvitations.forEach(invitation => {
      if (invitation.created_at) {
        const invDate = new Date(invitation.created_at)
        const monthKey = `${invDate.getFullYear()}-${String(invDate.getMonth() + 1).padStart(2, '0')}`
        const monthEntry = months.find(m => m.monthKey === monthKey)
        if (monthEntry) {
          monthEntry.count++
        }
      }
    })

    return months
  }, [users, pendingInvitations, monthFilter])

  // Process data for active vs pending pie chart and totals
  const { statusData, activeUserCount, pendingUserCount, archivedUserCount, totalUserCount } = useMemo(() => {
    const activeCount = users.filter(user => user.status === 'active').length
    const pendingCount = pendingInvitations.length
    const archivedCount = archivedUsers.length
    const totalCount = activeCount + pendingCount + archivedCount

    return {
      statusData: [
        { name: 'Active', value: activeCount, color: '#10B981' },
        { name: 'Pending', value: pendingCount, color: '#F59E0B' },
        ...(archivedCount > 0 ? [{ name: 'Archived', value: archivedCount, color: '#6B7280' }] : [])
      ].filter(item => item.value > 0), // Only show categories with values
      activeUserCount: activeCount,
      pendingUserCount: pendingCount,
      archivedUserCount: archivedCount,
      totalUserCount: totalCount
    }
  }, [users, pendingInvitations, archivedUsers])

  // Get invitation link
  const getInvitationLink = (token) => {
    // Use frontend URL for invitation links (not backend API URL)
    const frontendUrl = window.location.origin || 'http://localhost:3000'
    return `${frontendUrl}/accept-invitation/${token}`
  }

  // Copy invitation link
  const copyInvitationLink = (token, invitationId) => {
    const link = getInvitationLink(token)
    navigator.clipboard.writeText(link).then(() => {
      setCopiedInvitationId(invitationId)
      toast.success('Invitation link copied to clipboard!')
      setTimeout(() => setCopiedInvitationId(null), 2000)
    }).catch(() => {
      toast.error('Failed to copy link')
    })
  }

  // Open invitation link in browser
  const openInvitationLink = (token) => {
    const link = getInvitationLink(token)
    window.open(link, '_blank')
  }

  // Delete user mutation (soft delete - sets status to inactive)
  const deleteMutation = useMutation(
    (userId) => sasUsersAPI.delete(userId),
    {
      onSuccess: () => {
        toast.success('User Deleted successfully!')
        // Invalidate all sas-users queries to refresh both active and archived lists
        queryClient.invalidateQueries(['sas-users-all'])
        queryClient.invalidateQueries(['hub-user-invitations-pending'])
        // Refetch archived users to update the count immediately
        queryClient.refetchQueries(['sas-users-all', searchTerm, 'archived'])
        setShowDeleteConfirmation(false)
        setUserToDelete(null)
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.message || error.message || 'Failed to archive user'
        setErrorMessage(errorMsg)
        setShowErrorModal(true)
        setShowDeleteConfirmation(false)
      }
    }
  )

  // Update user status mutation
  const updateStatusMutation = useMutation(
    ({ userId, status, previousStatus }) => sasUsersAPI.updateStatus(userId, status),
    {
      onSuccess: (data, variables) => {
        // Use the message from the API response
        // Backend only returns "restored" when changing from archived to active
        // For inactive users changing to active, it returns "updated" (matching Organization Users behavior)
        toast.success(data?.data?.message || data?.message || 'User status updated successfully!')
        queryClient.invalidateQueries(['sas-users-all'])
      },
      onError: () => {
        toast.error('Failed to update user status')
      }
    }
  )

  // Restore user handler
  const handleRestoreUser = (userId, userName) => {
    setUserToRestore({ id: userId, name: userName })
    setShowRestoreModal(true)
  }

  const confirmRestoreUser = () => {
    if (userToRestore) {
      // Find the user to get their current status (should be archived)
      const user = archivedUsers.find(u => u.id === userToRestore.id)
      updateStatusMutation.mutate({ 
        userId: userToRestore.id, 
        status: 'active',
        previousStatus: user?.status || 'archived'
      })
      setShowRestoreModal(false)
      setUserToRestore(null)
    }
  }

  const cancelRestoreUser = () => {
    setShowRestoreModal(false)
    setUserToRestore(null)
  }

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
  }

  // Check if user is an admin (System Administrator or Primary Admin)
  const isAdminUser = (user) => {
    if (!user.role_name) return false
    
    const roleNameLower = user.role_name.toLowerCase().trim()
    return roleNameLower === 'admin' || 
           roleNameLower === 'system administrator' || 
           roleNameLower === 'administrator' || 
           roleNameLower.includes('admin')
  }

  const handleDeleteUser = (userId, userName) => {
    if (!canDeleteUsers) {
      toast.error("You don't have permission to delete users.")
      return
    }
    setUserToDelete({ id: userId, name: userName })
    setShowDeleteConfirmation(true)
  }

  const confirmDeleteUser = () => {
    if (!canDeleteUsers) {
      toast.error("You don't have permission to delete users.")
      return
    }
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id)
      setShowDeleteConfirmation(false)
      setUserToDelete(null)
    }
  }

  const handleEditUser = (user) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const handleEditSuccess = () => {
    setShowEditModal(false)
    setSelectedUser(null)
    queryClient.invalidateQueries(['sas-users-all'])
  }

  const handleStatusUpdate = (userId, newStatus) => {
    // Find the user to get their current status
    const user = users.find(u => u.id === userId) || archivedUsers.find(u => u.id === userId)
    updateStatusMutation.mutate({ 
      userId, 
      status: newStatus,
      previousStatus: user?.status
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      suspended: { color: 'bg-red-100 text-red-800', label: 'Suspended' },
      archived: { color: 'bg-gray-200 text-gray-700', label: 'Archived' }
    }
    
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never'
    
    try {
      const date = new Date(lastLogin)
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date'
      }
      
      const now = new Date()
      const diffInMs = now - date
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
      
      if (diffInMinutes < 1) return 'Just now'
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`
      if (diffInHours < 24) return `${diffInHours}h ago`
      if (diffInDays < 7) return `${diffInDays}d ago`
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`
      if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}mo ago`
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      console.error('Error formatting last login:', error)
      return 'Invalid date'
    }
  }

  // Check if invitation is expired
  const isInvitationExpired = (expiresAt) => {
    if (!expiresAt) return false
    try {
      const expiryDate = new Date(expiresAt)
      return new Date() > expiryDate
    } catch (error) {
      return false
    }
  }

  // Format expiry date
  const formatExpiryDate = (expiresAt) => {
    if (!expiresAt) return 'No expiry'
    try {
      const date = new Date(expiresAt)
      if (isNaN(date.getTime())) return 'Invalid date'
      
      const now = new Date()
      const diffInMs = date - now
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
      
      if (diffInDays < 0) {
        return `Expired ${Math.abs(diffInDays)} day${Math.abs(diffInDays) !== 1 ? 's' : ''} ago`
      } else if (diffInDays === 0) {
        return 'Expires today'
      } else if (diffInDays === 1) {
        return 'Expires tomorrow'
      } else if (diffInDays <= 7) {
        return `Expires in ${diffInDays} days`
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      }
    } catch (error) {
      return 'Invalid date'
    }
  }

  if (!isControlCenter()) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-secondary-900 mb-4">Access Denied</h2>
        <p className="text-secondary-600">You don't have permission to view this page.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Product Hub Users</h1>
          <p className="text-secondary-600 mt-1">Manage users who have access to the product hub</p>
        </div>
        {canInviteUsers && (
          <div className="mt-4 sm:mt-0">
            <Button onClick={() => {
              setSelectedOrganizationId(null) // For global invitation
              setShowInviteModal(true)
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Invite User
            </Button>
          </div>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        {/* Monthly User Additions Bar Chart */}
        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Users Added by Month
              </CardTitle>
              <div className="flex items-center gap-2">
                <label className="text-sm text-secondary-600">Last</label>
                <select
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(Number(e.target.value))}
                  className="input text-sm min-w-[8rem]"
                >
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                  <option value={24}>24 months</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyUserData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#3B82F6" name="Users Added" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Active vs Pending Pie Chart */}
        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <PieChart className="h-5 w-5 mr-2" />
                User Status Distribution
              </CardTitle>
              <div className="flex flex-wrap items-center gap-4 text-sm text-secondary-600">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-secondary-500">Total Users</span>
                  <span className="text-secondary-900 font-semibold">{totalUserCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 font-medium text-secondary-500">
                    <span className="h-2 w-2 rounded-full bg-green-500" />
                    Active
                  </span>
                  <span className="text-secondary-900 font-semibold">{activeUserCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 font-medium text-secondary-500">
                    <span className="h-2 w-2 rounded-full bg-yellow-500" />
                    Pending
                  </span>
                  <span className="text-secondary-900 font-semibold">{pendingUserCount}</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-secondary-100 bg-secondary-50/50 p-4">
                <p className="text-xs uppercase tracking-wide text-secondary-500">Total Users</p>
                <p className="text-2xl font-semibold text-secondary-900">{totalUserCount}</p>
              </div>
              <div className="rounded-lg border border-green-100 bg-green-50/50 p-4">
                <p className="text-xs uppercase tracking-wide text-green-600">Active Users</p>
                <p className="text-2xl font-semibold text-green-700">{activeUserCount}</p>
              </div>
              <div className="rounded-lg border border-yellow-100 bg-yellow-50/50 p-4">
                <p className="text-xs uppercase tracking-wide text-yellow-600">Pending Users</p>
                <p className="text-2xl font-semibold text-yellow-700">{pendingUserCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Tabs */}
      <div className="space-y-4">
        {/* Search */}
        <Card>
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <Input
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveSection('active')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeSection === 'active'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Active ({activeUserCount})
            </button>
            <button
              onClick={() => setActiveSection('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeSection === 'pending'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-yellow-600 hover:border-yellow-300'
              }`}
            >
              <Mail className="h-4 w-4 inline mr-2" />
              Pending ({pendingUserCount})
            </button>
            <button
              onClick={() => setActiveSection('archived')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeSection === 'archived'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-red-600 hover:border-red-300'
              }`}
            >
              <Archive className="h-4 w-4 inline mr-2" />
              Archived ({archivedUserCount})
            </button>
          </nav>
        </div>
      </div>

      {/* Pending Invitations Table */}
      {activeSection === 'pending' && pendingInvitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              Pending Invitations ({pendingInvitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expires
                    </th>
                    {canInviteUsers && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingInvitations.map((invitation) => {
                    const isExpired = isInvitationExpired(invitation.expires_at)
                    return (
                      <tr key={`invitation-${invitation.id}`} className={`hover:bg-yellow-50 transition-colors ${isExpired ? 'bg-red-50/30' : 'bg-yellow-50/30'}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isExpired ? 'bg-red-100' : 'bg-yellow-100'}`}>
                              {isExpired ? (
                                <AlertCircle className="h-5 w-5 text-red-600" />
                              ) : (
                                <Mail className="h-5 w-5 text-yellow-600" />
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {invitation.email}
                              </div>
                              <div className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {isExpired ? 'Expired Invitation' : 'Pending Invitation'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {invitation.role_name || 'No role'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            isExpired 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {isExpired ? 'Expired' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(invitation.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {invitation.expires_at ? (
                              <div className="flex flex-col">
                                <span className={isExpired ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                  {formatExpiryDate(invitation.expires_at)}
                                </span>
                                <span className="text-xs text-gray-500 mt-1">
                                  {new Date(invitation.expires_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">No expiry</span>
                            )}
                          </div>
                        </td>
                        {canInviteUsers && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyInvitationLink(invitation.token, invitation.id)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title={isExpired ? "Invitation expired - cannot copy link" : "Copy invitation link"}
                                disabled={isExpired}
                              >
                                {copiedInvitationId === invitation.id ? (
                                  <Check className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openInvitationLink(invitation.token)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title={isExpired ? "Invitation expired - link will not work" : "Open invitation link in browser"}
                                disabled={isExpired}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Pending Invitations Message */}
      {activeSection === 'pending' && pendingInvitations.length === 0 && !invitationsLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Invitations</h3>
            <p className="text-gray-600 mb-4">All invitations have been accepted or expired.</p>
            <Button onClick={() => {
              setSelectedOrganizationId(null)
              setShowInviteModal(true)
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Invite User
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active Users List */}
      {activeSection === 'active' && (
        isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Active Users ({users.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      {showActionColumn && (
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {(user.full_name || user.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.full_name || 'No name'}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.role_name || 'No role'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatLastLogin(user.last_login)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      {showActionColumn && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {canEditUsers && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditUser(user)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Edit User"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {canDeleteUsers && !isAdminUser(user) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteUser(user.id, user.full_name || user.email)}
                                disabled={deleteMutation.isLoading}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            {canDeleteUsers && isAdminUser(user) && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled
                                className="text-gray-400 cursor-not-allowed opacity-50"
                                title="Cannot delete System Administrator account"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        )
      )}

      {/* No Active Users Message */}
      {activeSection === 'active' && users.length === 0 && !isLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Users</h3>
            <p className="text-gray-600 mb-4">No product hub users match your search criteria.</p>
            <Button onClick={() => {
              setSelectedOrganizationId(null)
              setShowInviteModal(true)
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Invite First User
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Archived Users List */}
      {activeSection === 'archived' && (
        archivedUsersLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : archivedUsers.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Archive className="h-5 w-5 mr-2" />
                Archived Users ({archivedUsers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Deleted Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {archivedUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-600">
                                {(user.full_name || user.email).charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.full_name || 'No name'}
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.role_name || 'No role'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(user.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRestoreUser(user.id, user.full_name || user.email)}
                              disabled={updateStatusMutation.isLoading}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Restore User"
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Restore
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <Archive className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Archived Users</h3>
              <p className="text-gray-600">There are no archived users for this organization.</p>
            </CardContent>
          </Card>
        )
      )}

      {/* Invite Hub User Modal */}
      <InviteHubUserModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false)
          setSelectedOrganizationId(null)
        }}
        onSuccess={() => {
          setShowInviteModal(false)
          setSelectedOrganizationId(null)
          queryClient.invalidateQueries(['sas-users-all'])
          queryClient.invalidateQueries(['hub-user-invitations-pending'])
        }}
      />

      {/* Edit SAS User Modal */}
      <EditSASUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedUser(null)
        }}
        user={selectedUser}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false)
          setUserToDelete(null)
        }}
        onConfirm={confirmDeleteUser}
        title="Archive User"
        message={`Are you sure you want to Delete "${userToDelete?.name}"? `}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Restore User Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRestoreModal}
        onClose={cancelRestoreUser}
        onConfirm={confirmRestoreUser}
        title="Restore User"
        message={`Are you sure you want to restore "${userToRestore?.name}"? `}
        confirmText={updateStatusMutation.isLoading ? "Restoring..." : "Restore"}
        cancelText="Cancel"
        type="info"
      />

      {/* Error Modal */}
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false)
          setErrorMessage('')
        }}
        onConfirm={() => {
          setShowErrorModal(false)
          setErrorMessage('')
        }}
        title="Cannot Delete User"
        message={errorMessage || 'An error occurred while trying to delete the user.'}
        confirmText="Got it"
        cancelText={null}
        type="warning"
      />
    </div>
  )
}
